import type { Emitter } from "@core/events/emitter";
import type { FolderInfo } from "@models/data/folderDto";
import type { ProjectInfo } from "@models/data/projectInfo";

/**
 * A duplication in progress. Both ends are named because both are gated: the
 * copy is a placeholder that cannot be opened, and the source stops offering
 * actions while it is being read (Unity's `IsProjectDuplicating` answers for
 * either id).
 */
export interface ProjectDuplicationState {
  readonly sourceProjectId: string;
  readonly targetProjectId: string;
  /** The generated `"{name} Copy {n}"`, shown on the placeholder card. */
  readonly targetName: string;
}

/**
 * `IProjectListModel` — catalog of projects the user can browse and open.
 *
 * Distinct from {@link IProjectModel}, which represents the *currently
 * loaded* project (its scenes and entities). The list model owns the
 * grid you see on `ProjectsView`; clicking a card hands the chosen
 * `projectId` off to `IProjectModel.loadProjectAsync`.
 *
 * Unity reference: `ViewModels/ProjectsViewModel.cs` consumes
 * `ICustomerModel.GetFoldersAndProjectsAsync` and `IMultiuserAppModel`
 * events. The web port collapses that into a single
 * `IProjectListModel.refreshAsync` + `onChanged` emitter so the UI
 * doesn't have to fan out across multiple sources.
 */
export interface IProjectListModel {
  /** Latest snapshot of the catalog. Sorted in display order. */
  readonly projects: ReadonlyArray<ProjectInfo>;

  /** True while a `refreshAsync` is in flight. */
  readonly isLoading: boolean;

  /**
   * Last error from `refreshAsync`, or `null` if the most recent call
   * succeeded. Surfaced to the UI for retry / no-network banners.
   */
  readonly lastError: Error | null;

  readonly onChanged: Emitter<ReadonlyArray<ProjectInfo>>;
  readonly onLoadingChanged: Emitter<boolean>;

  /** Mirrors Unity's `DeleteProjectProgressViewModel` lifecycle. */
  readonly onBeginDeleteProject: Emitter<{ projectId: string; name: string }>;
  readonly onDeleteProjectProgress: Emitter<{ projectId: string; progress01: number }>;
  readonly onDeleteProjectCompleted: Emitter<{ projectId: string }>;
  readonly onDeleteProjectFailed: Emitter<{ projectId: string; error: Error }>;

  /**
   * Refresh the catalog from the underlying source. The in-memory
   * implementation resolves immediately; `HttpProjectListModel` hits
   * `IStorageModel.getProjectsByCustomer` (Dynamo `Query` against
   * `pk = "{customerId}:project"`).
   */
  refreshAsync(signal?: AbortSignal): Promise<ReadonlyArray<ProjectInfo>>;

  /** Look up a single project by id. `null` if not in the catalog. */
  getById(projectId: string): ProjectInfo | null;

  // ---- Mutations (in-memory implementation only — cloud overrides) --

  /**
   * Create a fresh draft. Returns the newly created `ProjectInfo`.
   * The card immediately appears in the list.
   *
   * `folderId` files it straight into a folder — Unity's `OnNewProject` puts a
   * new project into whichever folder is currently open
   * (`ProjectsViewModel.cs:3155-3158`), which is the only way to create a
   * project inside one.
   */
  createDraftAsync(name?: string, folderId?: string | null): Promise<ProjectInfo>;

  renameAsync(projectId: string, name: string): Promise<void>;

  /**
   * Copy a project, including every scene and asset under it — Unity's
   * `IProjectDuplicationModel.DuplicateProject`. Long-running: the work is done
   * by an admin lambda and the client waits for the clone to appear, so the
   * promise can be live for minutes. The copy's name is generated from the
   * source's (`"{name} Copy {n}"`), and `folderId` files it into a folder,
   * which Unity takes from the OPEN folder rather than the source's.
   *
   * Rejects when a duplication is already running: the lambda is not safe to
   * run twice at once, which is also why {@link isDuplicating} exists for the
   * UI to refuse the command up front rather than throwing.
   */
  duplicateAsync(projectId: string, folderId?: string | null): Promise<ProjectInfo | null>;

  /**
   * The duplication in flight, or `null` — Unity's `IsProcessing` +
   * `SourceProjectID` / `TargetProjectID` / `TargetName` on
   * `IProjectDuplicationModel`, which every gate upstream is derived from.
   * Changes are announced through `onChanged` (the placeholder card enters and
   * leaves the catalog with it).
   */
  readonly duplication: ProjectDuplicationState | null;

  deleteAsync(projectId: string): Promise<void>;

  /**
   * Inbound reconcile for a peer's `update_project` rename (tc 27) —
   * payload apply only, no persist.
   */
  applyProjectRename(projectId: string, name: string): void;

  /** Apply-only project console metadata refresh for local or peer writes. */
  applyProjectConsoleAsset(
    projectId: string,
    consoleAssetId: string | null,
    assetIds: ReadonlyArray<string>,
  ): void;

  /**
   * Apply-only metadata refresh for this client's own writes that bump the
   * project row timestamp outside the catalog model (for example scene
   * thumbnail uploads). Keeps the projects grid cache in step without waiting
   * for a full catalog reload, and credits the signed-in user as the card's
   * last editor (`modifiedByName`) — so never call it for a peer's write.
   */
  applyProjectModifiedDate(projectId: string, modifiedAt: string): void;

  // ---- Folders ------------------------------------------------------
  //
  // Folders live HERE rather than on their own model because Unity loads them
  // together for a reason that survives the port: a folder's membership is a
  // set of project ids, and deciding whether a folder is even VISIBLE requires
  // the resolved projects (`CustomerModel.MapProjectsToFolders` keeps a folder
  // only when it is your own, non-empty, or account-shared —
  // `CustomerModel.cs:1218-1245`). Splitting them into two models would mean
  // two loading states the UI has to combine and an ordering dependency between
  // them; one `refreshAsync` fetches both and joins them, exactly like
  // `GetFoldersAndProjectsAsync`.

  /** Visible folders for the account, after Unity's keep-rule. */
  readonly folders: ReadonlyArray<FolderInfo>;

  readonly onFoldersChanged: Emitter<ReadonlyArray<FolderInfo>>;

  getFolderById(folderId: string): FolderInfo | null;

  /**
   * The folder a project sits in, or `null` when it is at the root
   * (Unity `ICustomerModel.TryGetFolderForProject`).
   */
  getFolderForProject(projectId: string): FolderInfo | null;

  /**
   * Port of `CustomerModel.CreateFolderAsync` — mints the row, publishes
   * `update_folder`/Create, and prepends locally. `isShared` marks an
   * account-wide folder that stays visible to everyone even while empty.
   */
  createFolderAsync(isShared?: boolean): Promise<FolderInfo>;

  /** Port of `CustomerModel.UpdateFolderNameAsync`. */
  renameFolderAsync(folderId: string, name: string): Promise<void>;

  /**
   * Port of `CustomerModel.DeleteFolderAsync`, plus the caller-side branch
   * Unity keeps in `ProjectsViewModel.DeleteFolder(bool)`: when
   * `deleteProjectsInside` the contained projects are deleted FIRST, otherwise
   * they survive and reappear at the root (deleting the folder row and its map
   * rows is all that is needed for that — nothing on a project row names its
   * folder).
   */
  deleteFolderAsync(folderId: string, deleteProjectsInside: boolean): Promise<void>;

  /**
   * Move projects between folders — port of `ProjectsViewModel.MoveProjects`
   * fused with `CustomerModel.Add/RemoveProjectsFromFolderAsync`. `null` for
   * either side means the root. Both legs run, so a move from one folder to
   * another removes the old map rows and writes the new ones.
   */
  moveProjectsToFolderAsync(
    projectIds: ReadonlyArray<string>,
    targetFolderId: string | null,
  ): Promise<void>;

  /**
   * Inbound reconcile for a peer's `update_folder` (tc 57). Apply-only, never
   * persists. The four legs share one wire type — see `models/data/folderWire.ts`.
   */
  applyFolderEventFromCloud(folderEventType: number, wireFolder: unknown): void;
}

export const PROJECT_LIST_MODEL = Symbol.for("Campfire.ProjectListModel");
